/**
 * Système de permissions basé sur les rôles
 * Rôles: admin, manager, collaborateur
 */

// Récupérer l'utilisateur connecté depuis localStorage
export const getCurrentUser = () => {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    try {
        return JSON.parse(userStr);
    } catch (e) {
        return null;
    }
};

// Récupérer le rôle de l'utilisateur connecté
export const getUserRole = () => {
    const user = getCurrentUser();
    return user?.role ? user.role.toLowerCase() : null;
};

// Vérifier si l'utilisateur est admin
export const isAdmin = () => {
    return getUserRole() === 'admin';
};

// Vérifier si l'utilisateur est manager
export const isManager = () => {
    return getUserRole() === 'manager';
};

// Vérifier si l'utilisateur est collaborateur
export const isCollaborateur = () => {
    return getUserRole() === 'collaborateur';
};

// Vérifier si l'utilisateur a l'un des rôles spécifiés
export const hasRole = (roles) => {
    const userRole = getUserRole();
    if (!userRole) return false;
    return Array.isArray(roles) ? roles.includes(userRole) : roles === userRole;
};

// Permissions par fonctionnalité
export const can = (permission) => {
    const role = getUserRole();
    if (!role) return false;

    const permissions = {
        // Dashboard
        'view_dashboard': ['admin', 'manager', 'client'],
        'view_all_stats': ['admin', 'manager'],

        // Clients - STRICT: Seulement admin
        'view_clients': ['admin'], // Seul l'admin voit la liste des clients
        'create_client': ['admin'],
        'edit_client': ['admin'],
        'delete_client': ['admin'],

        // Projets
        'view_all_projects': ['admin', 'manager'],
        'view_assigned_projects': ['admin', 'manager', 'collaborateur', 'client'],
        'create_project': ['admin', 'manager'],
        'edit_project': ['admin', 'manager'],
        'delete_project': ['admin'],

        // Tickets
        'view_all_tickets': ['admin', 'manager'],
        'view_assigned_tickets': ['admin', 'manager', 'collaborateur', 'client'],
        'create_ticket': ['admin', 'manager'],
        'edit_ticket': ['admin', 'manager'],
        'delete_ticket': ['admin', 'manager'],
        'assign_ticket': ['admin', 'manager'],

        // Équipe
        'view_team': ['admin', 'manager'],
        'create_user': ['admin'],
        'edit_user': ['admin'],
        'delete_user': ['admin'],
        'view_teams': ['admin', 'manager'],
        'create_team': ['admin'],
        'edit_team': ['admin'],
        'delete_team': ['admin'],

        // Réunions
        'view_all_meetings': ['admin', 'manager'],
        'view_assigned_meetings': ['admin', 'manager', 'collaborateur'],
        'create_meeting': ['admin', 'manager'],
        'edit_meeting': ['admin', 'manager'],
        'delete_meeting': ['admin', 'manager'],

        // Paramètres
        'view_whatsapp_settings': ['admin'],
        'edit_whatsapp_settings': ['admin'],

        // Calendar
        'view_calendar': ['admin', 'manager', 'collaborateur', 'client'],

        // Points
        'view_points': ['admin', 'manager', 'collaborateur'],
        'view_point_rates': ['admin'],
    };

    const allowedRoles = permissions[permission];
    if (!allowedRoles) return false;

    return allowedRoles.includes(role);
};

// Configuration du menu selon le rôle
export const getMenuItems = () => {
    const rawRole = getUserRole();
    const role = rawRole ? rawRole.toLowerCase() : null;
    console.log("📂 MENU - Current Role detected:", role);

    const menuConfig = {
        admin: [
            'dashboard',
            'clients',
            'projects',
            'tickets',
            'ticket_requests', // Voir toutes les demandes
            'points',
            'point_rates',
            'team',
            'teams',
            'meetings',
            'calendar',
            'whatsapp_settings'
        ],
        manager: [
            'dashboard',
            'projects',
            'tickets',
            'my_tickets',
            'ticket_requests', // Voir toutes les demandes
            'points',
            'team',
            'teams',
            'meetings',
            'calendar'
        ],
        collaborateur: [
            'tickets',
            'projects',
            'points',
            'meetings',
            'calendar'
        ],
        client: [
            'my_projects', // Mes projets uniquement
            'my_ticket_requests', // Mes demandes de tickets
            'calendar'
        ]
    };

    // Si le rôle ne correspond à rien mais qu'on a un rôle, on donne un menu de base
    if (role && !menuConfig[role]) {
        // Fallback: si c'est 'admin' écrit bizarrement, on tente admin, sinon on donne accès collaborateur par défaut
        if (role.includes('admin')) return menuConfig.admin;
        return menuConfig.collaborateur;
    }

    // Par défaut si non authentifié ou rôle null: tableau vide
    return menuConfig[role] || [];
};
